// Datum zum Eintippen (19.10.2026, Dilsar): Erkennung für `components/DatumFeld.tsx`, ohne React und ohne Browser (mit Node getestet).
// Erkannt wird (alles ohne Rücksicht auf Groß-/Kleinschreibung, Heute ist ein Parameter):
//   „20.10“, „20.10.“, „2010“, „20/10“            → 20.10. des laufenden Jahres (mit `zukunft`: ist der Tag vorbei, nächstes Jahr)
//   „20.10.26“, „201026“, „20.10.2026“, „20102026“ → mit Jahr; „2026-10-20“ (ISO) geht auch
//   „heute“, „morgen“, „übermorgen“, „gestern“
//   „mo“ … „so“ oder „montag“ … „sonntag“         → der nächste solche Wochentag (nie heute, immer 1 bis 7 Tage später)
//   „+3“, „+3t“, „+2w“                             → in 3 Tagen, in 2 Wochen
//   „20“                                           → der 20. des laufenden (mit `zukunft`: bei vorbei nächsten) Monats
// Ergebnis `iso` (YYYY-MM-DD) oder null; `vollstaendig` = die Eingabe ist eindeutig fertig (Wort, +n, Datum mit Monat/Jahr), dann
// darf sie sofort übernommen werden; Teilstücke („2“, „20.1“) werden erst bei Enter oder Verlassen übernommen.

export interface DatumErgebnis {
  iso: string | null;
  vollstaendig: boolean;
}

const zwei = (n: number) => String(n).padStart(2, "0");

function tageImMonat(j: number, m: number): number {
  return new Date(Date.UTC(j, m, 0)).getUTCDate();
}

function iso(j: number, m: number, t: number): string | null {
  if (!Number.isInteger(j) || j < 1900 || j > 2999 || m < 1 || m > 12 || t < 1 || t > tageImMonat(j, m)) return null;
  return `${j}-${zwei(m)}-${zwei(t)}`;
}

function plus(datum: string, n: number): string {
  const [j, m, t] = datum.split("-").map(Number);
  const d = new Date(Date.UTC(j, m - 1, t + n));
  return `${d.getUTCFullYear()}-${zwei(d.getUTCMonth() + 1)}-${zwei(d.getUTCDate())}`;
}

function wochentagVon(datum: string): number {
  const [j, m, t] = datum.split("-").map(Number);
  return new Date(Date.UTC(j, m - 1, t)).getUTCDay();
}

const TAGE: Record<string, number> = {
  so: 0, sonntag: 0, mo: 1, montag: 1, di: 2, dienstag: 2, mi: 3, mittwoch: 3, do: 4, donnerstag: 4, fr: 5, freitag: 5, sa: 6, samstag: 6,
};

/** Namen der Wochentage für die Anzeige im Kalender (Montag zuerst) */
export const WOCHENTAGE_KURZ = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;
export const MONATE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"] as const;

/** YYYY-MM-DD → „20.10.2026“ (leer bleibt leer) */
export function datumAnzeige(wert: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(wert ?? "");
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
}

export function datumLesen(eingabe: string, heute: string, optionen: { zukunft?: boolean } = {}): DatumErgebnis {
  const roh = (eingabe ?? "").trim().toLowerCase();
  const nein: DatumErgebnis = { iso: null, vollstaendig: false };
  if (!roh) return nein;
  const [hj, hm] = heute.split("-").map(Number);
  const text = roh.replace(/\s+/g, "");

  // Wörter
  if (text === "heute") return { iso: heute, vollstaendig: true };
  if (text === "morgen") return { iso: plus(heute, 1), vollstaendig: true };
  if (text === "übermorgen" || text === "uebermorgen") return { iso: plus(heute, 2), vollstaendig: true };
  if (text === "gestern") return { iso: plus(heute, -1), vollstaendig: true };
  if (text.replace(/\.$/, "") in TAGE) {
    const ziel = TAGE[text.replace(/\.$/, "")];
    let diff = (ziel - wochentagVon(heute) + 7) % 7;
    if (diff === 0) diff = 7;
    return { iso: plus(heute, diff), vollstaendig: true };
  }
  // +n Tage, +nt, +nw
  const rel = /^\+(\d{1,3})([tw]?)$/.exec(text);
  if (rel) return { iso: plus(heute, Number(rel[1]) * (rel[2] === "w" ? 7 : 1)), vollstaendig: true };

  // ISO
  const isoM = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (isoM) {
    const r = iso(+isoM[1], +isoM[2], +isoM[3]);
    return { iso: r, vollstaendig: r !== null };
  }

  const jahr = (s: string): number => (s.length === 2 ? 2000 + Number(s) : Number(s));
  const ohneJahr = (t: number, m: number): string | null => {
    let r = iso(hj, m, t);
    if (r && optionen.zukunft && r < heute) r = iso(hj + 1, m, t);
    return r;
  };

  // mit Trennzeichen: 20.10 / 20.10. / 20.10.26 / 20.10.2026 / 20/10
  const mit = /^(\d{1,2})[./-](\d{1,2})(?:[./-](\d{2}|\d{4}))?\.?$/.exec(text);
  if (mit) {
    const t = +mit[1];
    const m = +mit[2];
    if (mit[3]) {
      const r = iso(jahr(mit[3]), m, t);
      return { iso: r, vollstaendig: r !== null };
    }
    const r = ohneJahr(t, m);
    // „20.1“ könnte noch „20.10“ werden: erst bei Enter/Verlassen übernehmen
    return { iso: r, vollstaendig: r !== null && (mit[2].length === 2 || /\.$/.test(text)) };
  }
  // nur Ziffern: 20 / 2010 / 201026 / 20102026 (3 Ziffern: 2.10 oder 20.1 sind unklar → 2.10)
  if (/^\d+$/.test(text)) {
    if (text.length <= 2) {
      const t = +text;
      let r = iso(hj, hm, t);
      if (r && optionen.zukunft && r < heute) r = hm === 12 ? iso(hj + 1, 1, t) : iso(hj, hm + 1, t);
      return { iso: r, vollstaendig: false };
    }
    if (text.length === 3) {
      const r = ohneJahr(+text.slice(0, 1), +text.slice(1));
      return { iso: r, vollstaendig: false };
    }
    if (text.length === 4) {
      const r = ohneJahr(+text.slice(0, 2), +text.slice(2));
      return { iso: r, vollstaendig: r !== null };
    }
    if (text.length === 6) {
      const r = iso(jahr(text.slice(4)), +text.slice(2, 4), +text.slice(0, 2));
      return { iso: r, vollstaendig: r !== null };
    }
    if (text.length === 8) {
      const r = iso(+text.slice(4), +text.slice(2, 4), +text.slice(0, 2));
      return { iso: r, vollstaendig: r !== null };
    }
  }
  return nein;
}

/** Tage eines Monats für den Kalender: Wochen mit je 7 Feldern, Montag zuerst; null = leeres Feld */
export function monatsRaster(jahr: number, monat: number): (string | null)[][] {
  const erster = new Date(Date.UTC(jahr, monat - 1, 1)).getUTCDay(); // 0 = So
  const vorlauf = (erster + 6) % 7;
  const tage = tageImMonat(jahr, monat);
  const felder: (string | null)[] = [...Array<null>(vorlauf).fill(null), ...Array.from({ length: tage }, (_, i) => iso(jahr, monat, i + 1))];
  while (felder.length % 7 !== 0) felder.push(null);
  const wochen: (string | null)[][] = [];
  for (let i = 0; i < felder.length; i += 7) wochen.push(felder.slice(i, i + 7));
  return wochen;
}

/** Monat ± n (Jahr/Monat) */
export function monatVerschieben(jahr: number, monat: number, n: number): { jahr: number; monat: number } {
  const z = jahr * 12 + (monat - 1) + n;
  return { jahr: Math.floor(z / 12), monat: (z % 12) + 1 };
}
